import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { User, Mail, Briefcase, Building2, Phone, Calendar, MapPin, Shield, Edit, Save, X } from 'lucide-react';

const EmployeeProfile = () => {
  const { token } = React.useContext(AuthContext);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingAddress, setEditingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState('');

  useEffect(() => {
    fetchEmployeeProfile();
  }, []);

  const fetchEmployeeProfile = async () => {
    try {
      const response = await axios.get(`${API}/employee/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployee(response.data);
      setNewAddress(response.data.home_address || '');
    } catch (error) {
      console.error('Failed to fetch employee profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const updateAddress = async () => {
    try {
      await axios.put(`${API}/employee/profile/address`, {
        home_address: newAddress
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Address updated successfully!');
      setEditingAddress(false);
      fetchEmployeeProfile();
    } catch (error) {
      console.error('Failed to update address:', error);
      toast.error('Failed to update address');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800 border-red-300';
      case 'manager': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'viewer': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-purple-100 text-purple-800 border-purple-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
        <Navigation />
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <Card className="glass max-w-2xl mx-auto">
            <CardContent className="py-12 text-center">
              <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Employee Profile Not Found</h2>
              <p className="text-gray-600">You don't have an employee profile.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            My Employee Profile
          </h1>

          {/* Basic Information */}
          <Card className="glass mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Basic Information</CardTitle>
                <Badge variant="outline" className={getRoleBadgeColor(employee.role)}>
                  <Shield className="w-3 h-3 mr-1" />
                  {employee.role?.toUpperCase()}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-rose-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Full Name</p>
                    <p className="font-medium">{employee.full_name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-rose-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium">{employee.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Briefcase className="w-5 h-5 text-rose-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Position</p>
                    <p className="font-medium">{employee.position}</p>
                  </div>
                </div>

                {employee.department && (
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-rose-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Department</p>
                      <p className="font-medium">{employee.department}</p>
                    </div>
                  </div>
                )}

                {employee.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-rose-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium">{employee.phone}</p>
                    </div>
                  </div>
                )}

                {employee.start_date && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-rose-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Start Date</p>
                      <p className="font-medium">{new Date(employee.start_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t">
                <Badge variant="outline" className={
                  employee.status === 'active' ? 'bg-green-100 text-green-800' :
                  employee.status === 'on_leave' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }>
                  Status: {employee.status?.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Home Address - Editable */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Home Address</CardTitle>
                {!editingAddress && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingAddress(true)}
                    className="text-rose-600 hover:bg-rose-50"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingAddress ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Address</label>
                    <Input
                      value={newAddress}
                      onChange={(e) => setNewAddress(e.target.value)}
                      placeholder="Enter your home address"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={updateAddress}
                      className="bg-rose-600 hover:bg-rose-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingAddress(false);
                        setNewAddress(employee.home_address || '');
                      }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-rose-600 mt-1" />
                  <div>
                    <p className="text-gray-700">
                      {employee.home_address || 'No address provided'}
                    </p>
                    {!employee.home_address && (
                      <p className="text-sm text-gray-500 mt-1">Click "Edit" to add your address</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Most of your profile information is managed by the administrator. 
              Only your home address can be updated by you. If you need to change other details, 
              please contact your administrator.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EmployeeProfile;
